import { Component, DestroyRef, computed, inject, type Signal } from "@angular/core";

import { AccessDeniedComponent } from "../../../shared/components/access-denied/access-denied.component";
import { AuthLayoutComponent } from "../../../shared/components/auth-layout/auth-layout.component";
import { AUTH_PROVIDER } from "../../../core/auth/auth-provider.token";
import { TENANT_CONTEXT } from "../../../core/auth/tenant-context.token";
import { AuthStateService } from "../../../core/services/auth-state.service";
import { BrandingService, DAVACO_BRANDING } from "../../../core/services/branding.service";
import { buildIncidentRef } from "../../../core/support/build-incident-ref";

import type { IPortalBranding } from "../../../core/models/i-portal-branding";
import type { IPortalUser } from "../../../core/models/i-portal-user";
import type { ITenantContext } from "../../../core/auth/i-tenant-context";

/**
 * "No access" — for anyone who is signed in but may not use THIS portal.
 *
 * Two audiences, one route, told apart by the host. On a CUSTOMER host (board
 * 7f) the visitor is signed in but not a member of that host's organization:
 * either Auth0 refused the account outright (an `access_denied` callback,
 * recorded as `AuthStateService.refusal`) or the token names a different
 * organization (`orgGuard`). That variant is tenant-branded, because the
 * visitor is looking at that customer's address, and the fix is a different
 * account, or being added to the organization. On the APEX, described below, it is a Davaco
 * employee without the admin role.
 *
 * Both render through app-access-denied, the shared board-7f card. The
 * customer variant sets it `featured` (the tenant logo leads, with motion) and
 * carries no incident code; only the staff variant shows one.
 *
 * On the apex it is the Entra door's refusal.
 *
 * Reached from `staffAccessGuard` by a Davaco employee who signed in
 * successfully but holds no Davaco Admin role. Sibling of ForbiddenComponent
 * and deliberately NOT the same screen: that one tells a customer they are on
 * the wrong organization's address, and its sentence ("isn't a member of
 * Starbucks") is nonsense for an employee on the apex, where there is no
 * organization to be a member of.
 *
 * The difference drives the one action too. A customer's fix is a different
 * account, so that screen offers sign-out. An employee's fix is an ADMINISTRATOR
 * granting them the role — signing in again with any account changes nothing —
 * so the primary line here asks them to request access, and sign-out is the
 * secondary way out for someone who used the wrong work account.
 */
@Component({
  selector: "app-no-access",
  standalone: true,
  imports: [AuthLayoutComponent, AccessDeniedComponent],
  templateUrl: "./no-access.component.html",
})
export class NoAccessComponent {

  private readonly provider = inject(AUTH_PROVIDER);

  private readonly brandingService = inject(BrandingService);

  private readonly authState = inject(AuthStateService);

  private readonly tenant: ITenantContext = inject(TENANT_CONTEXT);

  public readonly branding: Signal<IPortalBranding> = this.brandingService.branding;

  /** True on a customer subdomain: the membership refusal, not the staff one. */
  public readonly isCustomerHost: boolean = this.tenant.kind === "auth0";

  public constructor() {
    // A customer host keeps the customer's branding — the screen names that
    // organization, and the visitor is looking at its address.
    if (this.isCustomerHost) {
      return;
    }

    // Force the Davaco brand layer, whatever the session was wearing.
    //
    // BrandingService.load() resolves `readActingOrgSlug() ?? tenant.organization`
    // and runs in an app initializer BEFORE authentication, so an employee whose
    // previous session was viewing as a customer arrives on the apex already
    // branded as that customer — this screen would show Starbucks' logo and
    // green accent while its copy talks about the Davaco staff portal and the
    // Davaco Admin role.
    //
    // Unlike ForbiddenComponent, which is tenant-branded on purpose because the
    // visitor is looking at a specific customer's address, this is the apex. As
    // loadSlug() puts it for the same host, Davaco's own colors are the ANSWER
    // here, not a stand-in.
    //
    // Borrowed, not applied: this route is unguarded, so an admin viewing as a
    // customer can reach it and then go Back into the shell. A permanent
    // apply() left the shell in Davaco navy over that customer's rows (#661).
    //
    // The stored slug is deliberately left alone, so an admin who is later
    // granted the role and admitted still returns to the customer they were
    // viewing.
    this.brandingService.borrow(DAVACO_BRANDING, inject(DestroyRef));
  }

  /**
   * The account that was refused, or empty when the token carried none.
   *
   * Worth naming for the same reason as on the org refusal, plus one specific
   * to staff: Entra hands out tokens for ADM_ and day-to-day accounts alike,
   * and the role is usually assigned to only one of them. Seeing which one the
   * browser used is often the whole answer.
   */
  public readonly email: Signal<string> = computed((): string => {
    const user: IPortalUser | null = this.authState.user();
    return user?.email ?? "";
  });

  /**
   * `admin_role_required · ref xxxx-xx`.
   *
   * Derived, not generated, so it is stable across reloads and an administrator
   * can regenerate it from the subject and host — the same contract as the org
   * refusal's ref. See `buildIncidentRef`.
   */
  public readonly reference: Signal<string> = computed((): string => {
    const user: IPortalUser | null = this.authState.user();
    // After an Auth0 refusal there is no session, only the refused user id.
    const subject: string =
      user?.sub ?? this.authState.refusal()?.subject ?? "anonymous";
    return buildIncidentRef(subject, this.tenant.organization ?? "apex");
  });

  /**
   * Sign out, for someone who signed in with the wrong work account.
   *
   * Secondary here, unlike on the org refusal: the usual fix is a role grant,
   * not a different account, and a plain return to /login would silently
   * re-issue the same token and land right back on this screen.
   */
  public async signOut(): Promise<void> {
    await this.provider.logout();
  }
}
